import { useCallback, useEffect, useRef, useState } from "react";
import { httpClient } from "../../../shared/api";
import { API } from "../../../cache";
import { downloadAttachment } from "../../../shared/utils";

const ATT_ACCEPT = ".pdf,.xls,.xlsx,.csv,.doc,.docx,.txt,.jpg,.jpeg,.png,.mp4,.mov,.avi,.zip,.rar";
const ATT_MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;

function iconForType(t) {
  if (!t) return "📎";
  if (t.startsWith("image")) return "🖼";
  if (t.includes("pdf"))     return "📄";
  if (t.includes("sheet") || t.includes("excel") || t.includes("csv")) return "📊";
  if (t.includes("word")  || t.includes("doc"))   return "📝";
  return "📎";
}

/**
 * Attachments tab body for any entity that participates in the polymorphic
 * `/attachments` API (defect / feature / sub_feature / task / meeting).
 *
 * Props:
 *   refType / refId — polymorphic parent record
 *   caption        — caption stamped on each upload (defaults to refType)
 *   showToast      — (msg, severity) => void
 *
 * Uploads enforce a 2 MB cap; the file types accepted match the global
 * Attachment upload policy in docs/frontend.md.
 */
export default function EntityAttachmentsTab({ refType, refId, caption, showToast }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState("");
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!refId) return;
    setLoading(true);
    try {
      const data = await httpClient.get(`/attachments/${refType}/${refId}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems([]);
      showToast?.(err?.message || "Failed to load attachments", "error");
    } finally {
      setLoading(false);
    }
  }, [refType, refId, showToast]);

  useEffect(() => { load(); }, [load]);

  const resetFile = () => {
    setSelectedFile(null);
    setFileError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] ?? null;
    setFileError("");
    if (!f) { setSelectedFile(null); return; }
    if (f.size > ATT_MAX_FILE_SIZE_BYTES) {
      setFileError("File exceeds the 2 MB limit.");
      setSelectedFile(null);
      e.target.value = "";
      return;
    }
    setSelectedFile(f);
  };

  const upload = async () => {
    if (!selectedFile || !refId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      fd.append("ref_type", refType);
      fd.append("ref_id", String(refId));
      fd.append("caption", caption || refType);
      const res = await fetch(`${API}/attachments/upload`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `Upload failed (${res.status})`);
      resetFile();
      await load();
      showToast?.("Attachment uploaded", "success");
    } catch (err) {
      showToast?.(err?.message || "Failed to upload attachment", "error");
    } finally {
      setUploading(false);
    }
  };

  const download = async (att) => {
    try {
      await downloadAttachment(att.id);
    } catch (err) {
      showToast?.(err?.message || "Failed to download attachment", "error");
    }
  };

  const remove = async (att) => {
    if (!att?.id) return;
    const ok = window.confirm(`Delete ${att.file_name || "this attachment"}?`);
    if (!ok) return;
    setDeleting(att.id);
    try {
      await httpClient.delete(`/attachments/${att.id}`);
      setItems((prev) => (prev || []).filter((a) => a.id !== att.id));
    } catch (err) {
      showToast?.(err?.message || "Failed to delete attachment", "warn");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <>
      <div className="dfv2-att-toolbar">
        <div className="dfv2-att-upload">
          <input
            ref={fileInputRef}
            type="file"
            accept={ATT_ACCEPT}
            className="dfv2-att-file-input"
            onChange={handleFileChange}
          />
          <button
            type="button"
            className="dfv2-att-add-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {selectedFile ? "Change File" : "Choose File"}
          </button>
          <span className="dfv2-att-file-name">
            {selectedFile ? selectedFile.name : "No file selected"}
          </span>
          <button
            type="button"
            className="dfv2-att-upload-btn"
            onClick={upload}
            disabled={uploading || !selectedFile || !!fileError}
          >
            {uploading ? "Uploading…" : "Upload"}
          </button>
          {selectedFile && (
            <button type="button" className="dfv2-att-clear-btn" onClick={resetFile} disabled={uploading}>
              Clear
            </button>
          )}
        </div>
      </div>
      {fileError && <div className="dfv2-att-file-error">{fileError}</div>}
      <div className="dfv2-att-list">
        {loading ? (
          <div className="dfv2-att-empty">Loading…</div>
        ) : !items || items.length === 0 ? (
          <div className="dfv2-att-empty">No attachments yet.</div>
        ) : (
          items.map((a) => (
            <div key={a.id} className="dfv2-att-card">
              <span className="dfv2-att-icon">{iconForType(a.file_type)}</span>
              <div className="dfv2-att-info">
                <button
                  type="button"
                  onClick={() => download(a)}
                  className="dfv2-att-name"
                  title={a.file_name}
                  style={{ background: "none", border: "none", padding: 0, font: "inherit", textAlign: "left", cursor: "pointer" }}
                >
                  {a.file_name}
                </button>
                <div className="dfv2-att-meta">
                  {a.file_type && <span className="dfv2-att-tag">{a.file_type.split("/").pop()}</span>}
                  {a.file_size_kb > 0 && (
                    <span className="dfv2-att-tag dfv2-att-tag--size">
                      {a.file_size_kb < 1024 ? `${a.file_size_kb} KB` : `${(a.file_size_kb / 1024).toFixed(1)} MB`}
                    </span>
                  )}
                  {a.caption && <span className="dfv2-att-tag dfv2-att-tag--cat">{a.caption}</span>}
                  {a.uploader_name && <span className="dfv2-att-by">by {a.uploader_name}</span>}
                  {a.created_at && (
                    <span className="dfv2-att-date">
                      {new Date(a.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="dfv2-att-del"
                onClick={() => remove(a)}
                disabled={deleting === a.id}
                title="Delete attachment"
              >
                {deleting === a.id ? "…" : "✕"}
              </button>
            </div>
          ))
        )}
      </div>
    </>
  );
}
